import { useState } from "react";
import {
  Aviso,
  Botao,
  Cabecalho,
  Campo,
  CampoDeTelefone,
  Celula,
  CelulaCabecalho,
  Chip,
  Corpo,
  Linha,
  Tabela,
} from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  nomeDaOrganizacao,
  useAlterarPessoa,
  useOrganizacoesDoCadastro,
  usePessoasDaOrganizacao,
  type PessoaDoCadastro,
} from "../../nucleo/cadastro";

/** Pessoas de uma organização com cargo, telefone e superior hierárquico. */
export function PessoasDaOrganizacao({
  organizacaoId,
  podeEditar,
}: {
  organizacaoId: string;
  podeEditar: boolean;
}) {
  const pessoas = usePessoasDaOrganizacao(organizacaoId);
  const [editando, setEditando] = useState<PessoaDoCadastro | null>(null);

  if (pessoas.isPending) return <p role="status">Carregando pessoas…</p>;
  if (pessoas.isError) return <Aviso tom="erro">{mensagemDoConsole(pessoas.error)}</Aviso>;
  if (pessoas.data.length === 0) return <Aviso tom="marca">Nenhuma pessoa cadastrada nesta organização.</Aviso>;

  return (
    <div data-testid="pessoas-da-organizacao">
      <Tabela legenda="Pessoas da organização, com cargo, telefone e superior.">
        <Cabecalho>
          <Linha>
            <CelulaCabecalho>Pessoa</CelulaCabecalho>
            <CelulaCabecalho>Cargo</CelulaCabecalho>
            <CelulaCabecalho>Telefone</CelulaCabecalho>
            <CelulaCabecalho>Superior</CelulaCabecalho>
            <CelulaCabecalho>Situação</CelulaCabecalho>
            {podeEditar ? <CelulaCabecalho>Ação</CelulaCabecalho> : null}
          </Linha>
        </Cabecalho>
        <Corpo>
          {pessoas.data.map((pessoa) => (
            <Linha key={pessoa.id} data-testid={`pessoa-${pessoa.id}`}>
              <Celula>
                <b>{pessoa.nome}</b>
                <div className="celula-secundaria">{pessoa.email}</div>
              </Celula>
              <Celula>{pessoa.cargo ?? "—"}</Celula>
              <Celula>{pessoa.telefone ?? "—"}</Celula>
              <Celula>{pessoa.superior_nome ?? "—"}</Celula>
              <Celula>
                {!pessoa.ativo ? (
                  <Chip estado="erro">Inativa</Chip>
                ) : pessoa.acesso_definido ? (
                  <Chip estado="ok">Com acesso</Chip>
                ) : (
                  <Chip estado="aguardando">Sem acesso ainda</Chip>
                )}
              </Celula>
              {podeEditar ? (
                <Celula>
                  <Botao
                    tamanho="pequeno"
                    variante="fantasma"
                    onClick={() => setEditando(pessoa)}
                    aria-label={`Editar ${pessoa.nome}`}
                  >
                    Editar
                  </Botao>
                </Celula>
              ) : null}
            </Linha>
          ))}
        </Corpo>
      </Tabela>
      {editando ? (
        <EdicaoDePessoa
          pessoa={editando}
          colegas={pessoas.data.filter((item) => item.ativo && item.id !== editando.id)}
          aoFechar={() => setEditando(null)}
        />
      ) : null}
    </div>
  );
}

function EdicaoDePessoa({
  pessoa,
  colegas,
  aoFechar,
}: {
  pessoa: PessoaDoCadastro;
  colegas: PessoaDoCadastro[];
  aoFechar: () => void;
}) {
  const alterar = useAlterarPessoa();
  const [cargo, setCargo] = useState(pessoa.cargo ?? "");
  const [telefone, setTelefone] = useState(pessoa.telefone ?? "");
  const [superior, setSuperior] = useState(pessoa.superior_id ?? "");

  function salvar() {
    alterar.mutate(
      {
        id: pessoa.id,
        corpo: {
          cargo: cargo.trim() || null,
          telefone: telefone.trim() || null,
          superior_id: superior || null,
        },
      },
      { onSuccess: aoFechar },
    );
  }

  return (
    <section className="cartao mt-4" aria-label={`Editar ${pessoa.nome}`} data-testid="edicao-de-pessoa">
      <h3 className="cartao__titulo">{pessoa.nome}</h3>
      <div className="grid gap-3 md:grid-cols-3">
        <Campo rotulo="Cargo" value={cargo} onChange={(e) => setCargo(e.target.value)} />
        <CampoDeTelefone rotulo="Telefone" valor={telefone} aoMudar={setTelefone} />
        <div className="campo">
          <label className="campo__rotulo" htmlFor="superior-da-pessoa">
            Superior
          </label>
          <select
            id="superior-da-pessoa"
            className="campo__controle"
            value={superior}
            onChange={(e) => setSuperior(e.target.value)}
          >
            <option value="">Sem superior</option>
            {colegas.map((colega) => (
              <option key={colega.id} value={colega.id}>
                {colega.nome}
              </option>
            ))}
          </select>
        </div>
      </div>
      {alterar.isError ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(alterar.error)}</Aviso> : null}
      <div className="flex gap-2">
        <Botao onClick={salvar} disabled={alterar.isPending}>
          {alterar.isPending ? "Salvando…" : "Salvar"}
        </Botao>
        <Botao variante="fantasma" onClick={aoFechar}>
          Cancelar
        </Botao>
      </div>
    </section>
  );
}

/** A aba de pessoas: escolhe a organização e mostra o que `PessoasDaOrganizacao` mostra. */
export function PainelDePessoas({ podeEditar }: { podeEditar: boolean }) {
  const organizacoes = useOrganizacoesDoCadastro();
  const [escolhida, setEscolhida] = useState("");

  return (
    <div data-testid="painel-de-pessoas">
      <div className="campo mb-3">
        <label className="campo__rotulo" htmlFor="organizacao-das-pessoas">
          Organização
        </label>
        <select
          id="organizacao-das-pessoas"
          className="campo__controle"
          value={escolhida}
          onChange={(evento) => setEscolhida(evento.target.value)}
        >
          <option value="">Escolha uma organização</option>
          {(organizacoes.data ?? []).map((organizacao) => (
            <option key={organizacao.id} value={organizacao.id}>
              {nomeDaOrganizacao(organizacao)}
            </option>
          ))}
        </select>
      </div>
      {escolhida ? (
        <PessoasDaOrganizacao organizacaoId={escolhida} podeEditar={podeEditar} />
      ) : (
        <Aviso tom="marca">Escolha uma organização para ver as pessoas dela.</Aviso>
      )}
    </div>
  );
}
